/**
 * Widget Data Fetcher
 *
 * Provides unified data fetching for all dashboard widgets.
 * Works with the widget template system to fetch, transform, and cache data.
 *
 * Features:
 * - Automatic caching with configurable TTL
 * - Date range parameter handling
 * - Error handling with fallback values
 * - Batch fetching for efficiency
 * - Data source grouping to minimize API calls
 * - Retry logic for transient failures
 * - Strict data validation
 */

import { projectAPI, financeAPI, communicationAPI, timesheetAPI, apiCall } from '../../services/api';
import type { ApiResult } from '../../services/realApi';
import { PERMISSIONS } from '../../utils/permissions';
import {
  getServiceBreakdown,
  getPlatformOverview,
  getMonthlySpendTrend,
  getMonitoringOverview,
  getFinOpsMetrics,
  getBurnRateTrend,
} from '../../services/cloudopsApi';
import {
  getOverviewMetrics,
  getRealtimeMetrics,
  getCostBreakdown,
  getAgentRuns,
  getToolUsage,
  getSafetyStats,
  getIncidentStats,
  getAPMHealth,
  getTracesTimeseries
} from '../../services/aiOperationsApi';
import { catalogAPI, runsAPI, eventsAPI } from '../../services/dataopsApi';
import { DATA_SOURCES } from './widgetTemplates';
import { DateRange } from './dateRangeUtils';
import { GREEN, RED } from '../../constants/chartColors';
import {
  validateSourceData,
  safeValidateWidgetData,
} from './widgetValidators';

/** User object shape used in fetcher context */
interface FetcherUser {
  id?: string;
  email?: string | null;
  role?: string | null;
  tenantId?: string | null;
  projectId?: string | null;
  permissions?: Array<string | { name?: string }>;
}

/** Context passed to data fetchers */
interface FetcherContext {
  dateRange?: DateRange;
  user?: FetcherUser;
  clientId?: string | null;
  [key: string]: unknown;
}

/** Parsed date range parameters for API calls */
export interface DateRangeParams {
  start_date: string | null;
  end_date: string | null;
  startDate: Date | null;
  endDate: Date | null;
  days: number | null;
  hours: number;
  timeRange: string;
  [key: string]: string | number | Date | null;
}

/** Monthly spend item used in mergeMonthlySpend */
interface MonthlySpendItem {
  period?: string;
  month?: string;
  amount?: string | number;
  cost?: string | number;
  spend?: string | number;
}

/** Merged monthly spend bucket */
interface MergedMonthlyBucket {
  month: string;
  infrastructure: number;
  delivery: number;
}

// Cache configuration
const DEFAULT_CACHE_TTL = 60000; // 1 minute cache
let cacheTTL = DEFAULT_CACHE_TTL;
let dataSourceCache: Record<string, unknown> = {};
let cacheTimestamps: Record<string, number> = {};

/**
 * Configure cache TTL
 * @param {number} ttlMs - Cache time-to-live in milliseconds
 */
export const setCacheTTL = (ttlMs: number): void => {
  if (typeof ttlMs === 'number' && ttlMs > 0) {
    cacheTTL = ttlMs;
  }
};

/**
 * Get current cache TTL
 * @returns {number} Current cache TTL in milliseconds
 */
export const getCacheTTL = (): number => cacheTTL;

/** Widget template shape used by the fetcher */
interface Widget {
  id: string;
  name?: string;
  type?: string;
  dataSource: string;
  getData?: (sourceData: unknown, context: FetcherContext) => unknown;
}

/** Result returned by fetchWidgetData */
interface WidgetFetchResult {
  data: Record<string, unknown>;
  error?: string;
  errors?: string[];
  validationErrors?: string[];
  success: boolean;
}

/**
 * Fetch with retry logic for transient failures
 */
const fetchWithRetry = async <T>(fetchFn: () => Promise<T>, maxRetries = 2, delayMs = 1000): Promise<T> => {
  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fetchFn();
    } catch (error) {
      lastError = error;

      // Don't retry on 4xx errors (client errors)
      const errStatus = (error instanceof Object && 'response' in error && error.response instanceof Object && 'status' in error.response)
        ? (error.response as { status: number }).status
        : undefined;
      if (errStatus !== undefined && errStatus >= 400 && errStatus < 500) {
        throw error;
      }

      if (attempt < maxRetries) {
        // Exponential backoff
        await new Promise(resolve => setTimeout(resolve, delayMs * (attempt + 1)));
      }
    }
  }

  throw lastError;
};

/**
 * Get cached data for a data source
 * @param {string} cacheKey - Cache key
 * @returns {Object|null} Cached data or null if expired/missing
 */
const getCachedData = (cacheKey: string): unknown => {
  const now = Date.now();
  if (dataSourceCache[cacheKey] && (now - (cacheTimestamps[cacheKey] || 0)) < cacheTTL) {
    return dataSourceCache[cacheKey];
  }
  return null;
};

/**
 * Set cached data for a data source
 */
const setCachedData = (cacheKey: string, data: unknown): void => {
  dataSourceCache[cacheKey] = data;
  cacheTimestamps[cacheKey] = Date.now();
};

/**
 * Generate cache key from data source and date range
 */
const generateCacheKey = (dataSource: string, dateRange: DateRange | undefined, userId: string | undefined): string => {
  const dateKey = dateRange
    ? `${dateRange.preset || 'custom'}-${dateRange.startDate || ''}-${dateRange.endDate || ''}`
    : 'default';
  // Include the full global filter state in cache key so filter changes bust the cache
  const filterKey = (() => {
    try {
      const stored = localStorage.getItem('globalFilter');
      if (stored) {
        const { filterType, selectedId } = JSON.parse(stored);
        return `${filterType}-${selectedId || 'none'}`;
      }
      const projectId = localStorage.getItem('selectedProjectId');
      return projectId ? `project-${projectId}` : 'all';
    } catch { return 'all'; }
  })();
  return `${dataSource}-${userId || 'anon'}-${dateKey}-${filterKey}`;
};

/**
 * Convert date range to API parameters
 */
export const dateRangeToParams = (dateRange: DateRange | undefined): DateRangeParams => {
  if (!dateRange) {
    // Default to last 30 days
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    return {
      start_date: start.toISOString().split('T')[0],
      end_date: end.toISOString().split('T')[0],
      startDate: start,
      endDate: end,
      days: 30,
      hours: 720,
      timeRange: '30d',
    };
  }

  const now = new Date();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  let startDate: Date | null, endDate: Date | null, days: number | null;

  let hours = null; // For APM hourly presets

  switch (dateRange.preset) {
    case 'all_time':
      // For "All Time", use null to indicate no date filtering
      // All backends now properly handle null dates
      startDate = null;
      endDate = null;
      days = null;
      break;
    case 'last_1_hour':
      startDate = new Date(now.getTime() - 1 * 60 * 60 * 1000);
      endDate = now;
      hours = 1;
      days = null;
      break;
    case 'last_6_hours':
      startDate = new Date(now.getTime() - 6 * 60 * 60 * 1000);
      endDate = now;
      hours = 6;
      days = null;
      break;
    case 'last_24_hours':
      startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      endDate = now;
      hours = 24;
      days = 1;
      break;
    case 'today':
      startDate = today;
      endDate = now;
      days = 1;
      break;
    case 'last_7_days':
      startDate = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);
      endDate = now;
      days = 7;
      break;
    case 'last_30_days':
      startDate = new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000);
      endDate = now;
      days = 30;
      break;
    case 'last_90_days':
      startDate = new Date(today.getTime() - 89 * 24 * 60 * 60 * 1000);
      endDate = now;
      days = 90;
      break;
    case 'this_month': {
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = now;
      days = Math.ceil((now.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
      break;
    }
    case 'last_month': {
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      endDate = new Date(now.getFullYear(), now.getMonth(), 0);
      endDate.setHours(23, 59, 59, 999);
      days = Math.ceil((endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
      break;
    }
    case 'this_year': {
      startDate = new Date(now.getFullYear(), 0, 1);
      endDate = now;
      days = Math.ceil((now.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
      break;
    }
    case 'last_year': {
      startDate = new Date(now.getFullYear() - 1, 0, 1);
      endDate = new Date(now.getFullYear() - 1, 11, 31);
      endDate.setHours(23, 59, 59, 999);
      days = Math.ceil((endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
      break;
    }
    case 'this_quarter': {
      const currentQuarter = Math.floor(now.getMonth() / 3);
      startDate = new Date(now.getFullYear(), currentQuarter * 3, 1);
      endDate = now;
      days = Math.ceil((now.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
      break;
    }
    case 'last_quarter': {
      const lastQuarter = Math.floor(now.getMonth() / 3) - 1;
      const quarterYear = lastQuarter < 0 ? now.getFullYear() - 1 : now.getFullYear();
      const adjustedQuarter = lastQuarter < 0 ? 3 : lastQuarter;
      startDate = new Date(quarterYear, adjustedQuarter * 3, 1);
      endDate = new Date(quarterYear, (adjustedQuarter + 1) * 3, 0);
      endDate.setHours(23, 59, 59, 999);
      days = Math.ceil((endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
      break;
    }
    case 'custom':
      if (dateRange.startDate && dateRange.endDate) {
        startDate = new Date(dateRange.startDate);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(dateRange.endDate);
        endDate.setHours(23, 59, 59, 999);
        days = Math.ceil((endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
      } else {
        startDate = new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000);
        endDate = now;
        days = 30;
      }
      break;
    default:
      startDate = new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000);
      endDate = now;
      days = 30;
  }

  // Map preset to APM timeRange format
  const getTimeRangeString = () => {
    switch (dateRange.preset) {
      case 'last_1_hour': return '1h';
      case 'last_6_hours': return '6h';
      case 'last_24_hours': return '24h';
      case 'last_7_days': return '7d';
      case 'last_30_days': return '30d';
      case 'last_90_days': return '90d';
      case 'this_year': return 'this_year';
      case 'all_time': return 'all_time';
      default: return days ? `${days}d` : '30d';
    }
  };

  // All backends now handle null dates properly, so we can use null consistently
  // Send full ISO timestamps to preserve time component for accurate filtering
  return {
    start_date: startDate?.toISOString() ?? null,
    end_date: endDate?.toISOString() ?? null,
    startDate,  // null for "All Time" filter
    endDate,    // null for "All Time" filter
    days,
    hours: hours || (days ? days * 24 : 17520), // 17520 = 2 years for "All Time"
    timeRange: getTimeRangeString(), // APM-compatible format
  };
};

/**
 * Fetch raw data for a specific data source
 * This is the low-level fetcher that gets raw API data
 */
export const fetchDataSourceData = async (dataSource: string, context: FetcherContext): Promise<unknown> => {
  const { dateRange, user, clientId } = context;


  const cacheKey = generateCacheKey(dataSource, dateRange, user?.id);

  // Check cache first
  const cached = getCachedData(cacheKey);
  if (cached) {
    return cached;
  }

  const params = dateRangeToParams(dateRange);
  let data = null;

  try {
    switch (dataSource) {
      case DATA_SOURCES.HUB:
        data = await fetchHubSourceData(user, clientId, params);
        break;

      case DATA_SOURCES.AWS_INSIGHTS:
        data = await fetchAWSInsightsData(user, clientId, params);
        break;

      case DATA_SOURCES.PLATFORMOPS:
        data = await fetchPlatformOpsSourceData(user, clientId, params);
        break;

      case DATA_SOURCES.GENAIOPS:
        data = await fetchGenAIOpsSourceData(user, clientId, params);
        break;

      case DATA_SOURCES.AGENTOPS:
        data = await fetchAgentOpsSourceData(user, clientId, params);
        break;

      case DATA_SOURCES.DATAOPS:
        data = await fetchDataOpsSourceData(user, clientId, params);
        break;

      case DATA_SOURCES.HEALTH:
        data = await fetchHealthSourceData(user, clientId, params);
        break;

      default:
        data = null;
    }

    if (data) {
      setCachedData(cacheKey, data);
    }

    return data;
  } catch (error) {
    console.error(`Error fetching data source ${dataSource}:`, error);
    return null;
  }
};

/**
 * Merge infrastructure and delivery monthly costs
 * Combines costs from PlatformOps (infrastructure) and finance API (delivery/timesheets)
 *
 * @param {Array} infrastructureTrend - Array with { period: "2026-01", amount: 1234 }
 * @param {Array} deliveryBreakdown - Array with { month: "2026-01", spend: 4000 }
 * @returns {Array} Merged array with { month: "2026-01", spend: 5234 }
 */
const mergeMonthlySpend = (infrastructureTrend: MonthlySpendItem[] = [], deliveryBreakdown: MonthlySpendItem[] = []) => {
  const monthlyMap: Record<string, MergedMonthlyBucket> = {};

  // Add infrastructure costs
  infrastructureTrend.forEach(item => {
    const month = item.period || item.month;
    if (month) {
      monthlyMap[month] = {
        month,
        infrastructure: Number(item.amount || item.cost || 0),
        delivery: 0,
      };
    }
  });

  // Add delivery costs
  deliveryBreakdown.forEach(item => {
    const month = item.month || item.period;
    if (month) {
      if (!monthlyMap[month]) {
        monthlyMap[month] = {
          month,
          infrastructure: 0,
          delivery: 0,
        };
      }
      monthlyMap[month].delivery = Number(item.spend || item.amount || 0);
    }
  });

  // Calculate total spend for each month
  const result = Object.values(monthlyMap)
    .map(item => ({
      month: item.month,
      spend: item.infrastructure + item.delivery,
      infrastructure: item.infrastructure,
      delivery: item.delivery,
    }))
    .sort((a, b) => a.month.localeCompare(b.month));
  return result;
};

/**
 * Fetch Hub data source (basic - projects, finance, notifications, timesheets)
 */
const fetchHubBasicData = async (user: FetcherUser | undefined, clientId: string | null | undefined, params: DateRangeParams): Promise<Record<string, unknown>> => {
  // Fetch all data in parallel
  const [projectsResult, costResult, monthlyTrendResult, notificationsResult, hoursByPersonResult] = await Promise.all([
    projectAPI.getProjects(user?.role ?? '', null, user?.projectId ?? null, clientId ?? null).catch(() => ({ success: false })),
    financeAPI.getCostAnalytics(user?.role ?? null, user?.tenantId ?? null, user?.projectId ?? null, params.start_date, params.end_date).catch(() => ({ success: false })),
    getMonthlySpendTrend({ clientId: clientId ?? undefined, timePeriod: 'last_3_months' }).catch(() => []),
    communicationAPI.getNotifications(user?.id).catch(() => ({ success: false })),
    timesheetAPI.getHoursByPerson(Object.fromEntries(
      Object.entries({ client_id: clientId, start_date: params.start_date, end_date: params.end_date, limit: String(100) })
        .filter(([, v]) => v != null) as [string, string][]
    )).catch(() => ({ success: false, data: [] }))
  ]);

  const allProjects = projectsResult.success ? ((projectsResult as ApiResult<unknown[]>).data || []) : [];
  const costAnalytics = costResult.success ? (costResult as ApiResult<Record<string, Record<string, unknown>>>).data : null;
  const infrastructureMonthlyTrend = Array.isArray(monthlyTrendResult) ? monthlyTrendResult : [];
  const allNotifications = notificationsResult.success ? ((notificationsResult as ApiResult<unknown[]>).data || []) : [];

  // Hours by person data from dedicated endpoint
  const hoursByPerson = hoursByPersonResult.success
    ? ((hoursByPersonResult as ApiResult<Array<{ name: string; hours: number }>>).data || []).map((p) => ({ name: p.name, hours: p.hours }))
    : [];

  // Filter by date range
  const { startDate, endDate } = params;
  const projects = filterByDateRange(allProjects as Record<string, unknown>[], startDate, endDate, 'updatedAt');
  const notifications = filterByDateRange(allNotifications as Record<string, unknown>[], startDate, endDate, 'timestamp');

  // Count active projects (not completed or cancelled) - use filtered projects for date-aware count
  const inactiveStatuses = new Set(['completed', 'cancelled', 'archived', 'closed']);
  const activeProjectsCount = projects.filter(p =>
    !p.status || !inactiveStatuses.has((p.status as string).toLowerCase())
  ).length;

  // Get budget and spend from finance API analytics (respects date filtering)
  const totalBudget = (costAnalytics?.summary?.total_budget as number) || 0;
  const totalSpent = (costAnalytics?.summary?.estimated_spend as number) || 0;
  const remainingBudget = (costAnalytics?.summary?.remaining_budget as number) || 0;

  // Calculate burn rate
  const burnRate = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0;

  // Merge infrastructure costs (from PlatformOps) with delivery costs (from finance API)
  const monthlySpend = mergeMonthlySpend(
    infrastructureMonthlyTrend,
    (costAnalytics?.monthly_breakdown as unknown as MonthlySpendItem[]) || []
  );

  return {
    projects,
    allProjects,
    costAnalytics,
    notifications,
    allNotifications,
    hoursByPerson,
    // Pre-calculated metrics from finance API (date-filtered)
    activeProjects: activeProjectsCount,
    totalProjects: projects.length,  // Use filtered projects count
    totalBudget,
    totalSpent,
    burnRate,
    remainingBudget,
    monthlySpend,
  };
};

/**
 * Fetch Hub Command Center data - aggregates data from ALL modules
 * This is the main data source for the Hub executive dashboard
 */
const fetchHubSourceData = async (user: FetcherUser | undefined, clientId: string | null | undefined, params: DateRangeParams): Promise<Record<string, unknown>> => {
  // Fetch all module data in parallel
  const [hubBasic, genaiopsData, agentopsData, dataopsData, platformopsData, awsInsightsData] = await Promise.all([
    fetchHubBasicData(user, clientId, params).catch(() => ({})),
    fetchGenAIOpsSourceData(user, clientId, params).catch(() => null),
    fetchAgentOpsSourceData(user, clientId, params).catch(() => null),
    fetchDataOpsSourceData(user, clientId, params).catch(() => null),
    fetchPlatformOpsSourceData(user, clientId, params).catch(() => null),
    fetchAWSInsightsData(user, clientId, params).catch(() => null),
  ]);

  // Cast module data to typed records for numeric calculations
  type NumRecord = Record<string, number | null | undefined>;
  const gd = genaiopsData as NumRecord | null;
  const ad = agentopsData as NumRecord | null;
  const dd = dataopsData as NumRecord | null;
  const pd = platformopsData as NumRecord | null;

  // Calculate aggregate system health score
  const healthScores = [
    gd?.qualityScore,
    ad?.healthScore,
    pd?.complianceScore,
    (dd?.totalRuns ?? 0) > 0 ? (((dd?.successfulRuns ?? 0) / (dd?.totalRuns ?? 1)) * 100) : null,
  ].filter(s => s != null && s > 0) as number[];
  const systemHealthScore = healthScores.length > 0
    ? Math.round(healthScores.reduce((a, b) => a + b, 0) / healthScores.length)
    : 0;

  // Calculate unified total cost (LLM + Cloud + Projects)
  const unifiedTotalCost =
    (gd?.totalCost || 0) +
    (pd?.totalCloudSpend || 0) +
    ((hubBasic as NumRecord)?.totalSpent || 0);

  // Calculate total active operations
  const totalActiveOperations =
    (ad?.activeRuns || 0) +
    (dd?.runningRuns || 0) +
    (pd?.activeEnvironments || 0);

  // Calculate platform-wide error rate
  const errorRates = [
    gd?.errorRate,
    ad?.successRate == null ? null : (100 - (ad.successRate ?? 0)),
    (dd?.totalRuns ?? 0) > 0 ? (((dd?.failedRuns ?? 0) / (dd?.totalRuns ?? 1)) * 100) : null,
  ].filter(r => r != null) as number[];
  const platformErrorRate = errorRates.length > 0
    ? Math.round((errorRates.reduce((a, b) => a + b, 0) / errorRates.length) * 100) / 100
    : 0;

  // Determine module health status
  const getModuleStatus = (score: number | null | undefined, errorIndicator = 0) => {
    if (score == null) return 'unavailable';
    if (score >= 80 && errorIndicator < 5) return 'healthy';
    if (score >= 50 || errorIndicator < 15) return 'warning';
    return 'critical';
  };

  const moduleHealth = {
    genaiops: gd ? getModuleStatus(gd.qualityScore, gd.errorRate ?? undefined) : 'unavailable',
    agentops: ad ? getModuleStatus(ad.healthScore, 100 - (ad.successRate ?? 100)) : 'unavailable',
    dataops: (dd?.totalRuns ?? 0) > 0
      ? getModuleStatus(((dd?.successfulRuns ?? 0) / (dd?.totalRuns ?? 1)) * 100, ((dd?.failedRuns ?? 0) / (dd?.totalRuns ?? 1)) * 100)
      : 'unavailable',
    platformops: pd ? getModuleStatus(pd.complianceScore, pd.openViolations ?? undefined) : 'unavailable',
  };

  // Collect cross-module alerts
  const crossModuleAlerts = [];
  if ((gd?.errorRate ?? 0) > 5) {
    crossModuleAlerts.push({ module: 'GenAIOps', severity: 'warning', message: `High error rate: ${(gd?.errorRate ?? 0).toFixed(1)}%` });
  }
  if ((ad?.safetyViolations ?? 0) > 0) {
    crossModuleAlerts.push({ module: 'AgentOps', severity: 'critical', message: `${ad?.safetyViolations} safety violations` });
  }
  if ((pd?.openViolations ?? 0) > 10) {
    crossModuleAlerts.push({ module: 'CloudOps', severity: 'warning', message: `${pd?.openViolations} open compliance violations` });
  }
  if ((dd?.failedRuns ?? 0) > 5) {
    crossModuleAlerts.push({ module: 'DataOps', severity: 'warning', message: `${dd?.failedRuns} failed pipeline runs` });
  }
  if ((gd?.totalCost ?? 0) > 100) {
    crossModuleAlerts.push({ module: 'GenAIOps', severity: 'info', message: `LLM costs: $${(gd?.totalCost ?? 0).toFixed(2)}` });
  }

  return {
    // Basic Hub data
    ...hubBasic,

    // Raw module data for detailed widgets
    genaiops: genaiopsData,
    agentops: agentopsData,
    dataops: dataopsData,
    platformops: platformopsData,

    // Executive aggregate metrics
    systemHealthScore,
    unifiedTotalCost,
    totalActiveOperations,
    platformErrorRate,

    // Module health status
    moduleHealth,

    // Cross-module alerts
    crossModuleAlerts,

    // Quick access metrics for widgets
    llmCost: gd?.totalCost || 0,
    llmTraces: gd?.totalTraces || 0,
    llmQuality: gd?.qualityScore || 0,
    agentRuns: ad?.totalRuns || 0,
    agentActiveRuns: ad?.activeRuns || 0,
    agentSuccessRate: ad?.successRate || 0,
    pipelineSuccessRate: (dd?.totalRuns ?? 0) > 0
      ? Math.round(((dd?.successfulRuns ?? 0) / (dd?.totalRuns ?? 1)) * 100)
      : 0,
    cloudCompliance: pd?.complianceScore || 0,
    cloudSpend: pd?.totalCloudSpend || 0,

    // AWS Insights data (for AWS/Partner users)
    projectHealthMap: awsInsightsData?.projectHealthMap,
    infrastructureSpend: awsInsightsData?.infrastructureSpend,
    fundingSummary: awsInsightsData?.fundingSummary,
    creditsOverview: awsInsightsData?.creditsOverview,
    fundingByProgram: awsInsightsData?.fundingByProgram,
    fundingTimeline: awsInsightsData?.fundingTimeline,
    creditsByClient: awsInsightsData?.creditsByClient,
    projectsAtRisk: awsInsightsData?.projectsAtRisk,
  };
};

/**
 * Fetch AWS Insights dashboard data
 * Only accessible to AWS and Partner users
 */
const fetchAWSInsightsData = async (user: FetcherUser | undefined, clientId: string | null | undefined, _params: DateRangeParams): Promise<Record<string, unknown> | null> => {
  if (!user) {
    return null;
  }

  const userPermissions = Array.isArray(user?.permissions)
    ? user.permissions
        .map((permission: string | { name?: string }) => (typeof permission === 'string' ? permission : permission?.name))
        .filter(Boolean)
    : [];

  const canViewAwsInsights =
    userPermissions.includes(PERMISSIONS.AWS_ACCESS_VIEW) ||
    userPermissions.includes(PERMISSIONS.AWS_ACCESS_MANAGE) ||
    userPermissions.includes(PERMISSIONS.PARTNERS_VIEW) ||
    userPermissions.includes(PERMISSIONS.PARTNERS_MANAGE);

  if (!canViewAwsInsights) {
    return null;
  }

  const baseUrl = '/dashboard/aws-insights';

  try {
    // For widgets that respect client filter, add client_id param
    const clientParam = clientId ? `?client_id=${clientId}` : '';

    // Fetch all AWS Insights endpoints in parallel
    const [
      projectHealthMap,
      infrastructureSpend,
      fundingSummary,
      creditsOverview,
      fundingByProgram,
      fundingTimeline,
      creditsByClient,
      projectsAtRisk
    ] = await Promise.all([
      // Project health heatmap - NEVER filtered
      apiCall(`${baseUrl}/project-health-map`).catch(() => null),
      // Infrastructure spend - respects filter
      apiCall(`${baseUrl}/infrastructure-spend${clientParam}`).catch(() => null),
      // Funding summary - respects filter
      apiCall(`${baseUrl}/funding-summary${clientParam}`).catch(() => null),
      // Credits overview - respects filter
      apiCall(`${baseUrl}/credits-overview${clientParam}`).catch(() => null),
      // Funding by program - respects filter
      apiCall(`${baseUrl}/funding-by-program${clientParam}`).catch(() => null),
      // Funding timeline - respects filter
      apiCall(`${baseUrl}/funding-timeline${clientParam}`).catch(() => null),
      // Credits by client - respects filter
      apiCall(`${baseUrl}/credits-by-client${clientParam}`).catch(() => null),
      // Projects at risk - respects filter
      apiCall(`${baseUrl}/projects-at-risk${clientParam}`).catch(() => null),
    ]);

    return {
      projectHealthMap,
      infrastructureSpend,
      fundingSummary,
      creditsOverview,
      fundingByProgram,
      fundingTimeline,
      creditsByClient,
      projectsAtRisk,
    };
  } catch (error) {
    console.error('Error fetching AWS Insights data:', error);
    return null;
  }
};

/**
 * Fetch Health dashboard data
 * Cross-client view for users with AWS_ACCESS_* or PARTNERS_* permissions
 * Shows project delivery, budgets, and support health across all accessible clients
 */
const fetchHealthSourceData = async (user: FetcherUser | undefined, _clientId: string | null | undefined, _params: DateRangeParams): Promise<Record<string, unknown> | null> => {
  if (!user) return null;

  const userPermissions = Array.isArray(user?.permissions)
    ? user.permissions
        .map((p: string | { name?: string }) => (typeof p === 'string' ? p : p?.name))
        .filter(Boolean)
    : [];
  const canViewHealth =
    userPermissions.includes(PERMISSIONS.AWS_ACCESS_VIEW) ||
    userPermissions.includes(PERMISSIONS.AWS_ACCESS_MANAGE) ||
    userPermissions.includes(PERMISSIONS.PARTNERS_VIEW) ||
    userPermissions.includes(PERMISSIONS.PARTNERS_MANAGE);
  if (!canViewHealth) {
    return null;
  }

  const baseUrl = '/dashboard/health';

  try {
    // Fetch all Health endpoints in parallel
    // IMPORTANT: Never pass client_id - always cross-client view
    const [
      deliveryMetrics,
      budgetMetrics,
      supportMetrics,
      projectsAtRisk,
      heatmapData,
      fundingByProgram,
      creditsByClient,
      creditExpiration,
      fundingRequests
    ] = await Promise.all([
      apiCall(`${baseUrl}/delivery-metrics`).catch(err => { console.error('delivery-metrics error:', err); return null; }),
      apiCall(`${baseUrl}/budget-metrics`).catch(err => { console.error('budget-metrics error:', err); return null; }),
      apiCall(`${baseUrl}/support-metrics`).catch(err => { console.error('support-metrics error:', err); return null; }),
      apiCall(`${baseUrl}/projects-at-risk`).catch(err => { console.error('projects-at-risk error:', err); return null; }),
      apiCall(`${baseUrl}/project-health-map`).catch(err => { console.error('project-health-map error:', err); return null; }),
      apiCall(`${baseUrl}/funding-by-program`).catch(err => { console.error('funding-by-program error:', err); return null; }),
      apiCall(`${baseUrl}/credits-by-client`).catch(err => { console.error('credits-by-client error:', err); return null; }),
      apiCall(`${baseUrl}/credit-expiration`).catch(err => { console.error('credit-expiration error:', err); return null; }),
      apiCall(`${baseUrl}/funding-requests`).catch(err => { console.error('funding-requests error:', err); return null; }),
    ]);


    return {
      deliveryMetrics,
      budgetMetrics,
      supportMetrics,
      projectsAtRisk,
      heatmapData,
      fundingByProgram,
      creditsByClient,
      creditExpiration,
      fundingRequests,
    };
  } catch (error) {
    console.error('Error fetching Health dashboard data:', error);
    return null;
  }
};

/**
 * Fetch PlatformOps data source
 */
const fetchPlatformOpsSourceData = async (_user: FetcherUser | undefined, clientId: string | null | undefined, params: DateRangeParams): Promise<Record<string, unknown>> => {
  // Determine time period for FinOps APIs based on date range
  const finopsTimePeriod = params.start_date ? 'custom' : 'all_time';
  // FinOps backend expects YYYY-MM-DD, not full ISO timestamps
  const finopsStartDate = params.start_date?.split('T')[0] ?? undefined;
  const finopsEndDate = params.end_date?.split('T')[0] ?? undefined;

  const [overview, serviceBreakdown, monitoringOverview, finopsMetrics, burnRateTrend] = await Promise.all([
    getPlatformOverview({ client_id: clientId ?? undefined }).catch(() => null),
    getServiceBreakdown({
      clientId: clientId ?? undefined,
      timePeriod: finopsTimePeriod,
      startDate: finopsStartDate,
      endDate: finopsEndDate,
    }).catch(() => []),
    getMonitoringOverview(clientId ?? undefined).catch(() => null),
    getFinOpsMetrics({
      clientId: clientId ?? undefined,
      timePeriod: finopsTimePeriod,
      startDate: finopsStartDate,
      endDate: finopsEndDate,
    }).catch(() => null),
    getBurnRateTrend({
      clientId: clientId ?? undefined,
      timePeriod: finopsTimePeriod,
      startDate: finopsStartDate,
      endDate: finopsEndDate,
    }).catch(() => []),
  ]);

  const totalCloudSpend = Array.isArray(serviceBreakdown)
    ? serviceBreakdown.reduce((sum, item) => sum + Number.parseFloat(item.cost || 0), 0)
    : 0;

  return {
    overview,
    serviceBreakdown,
    totalCloudSpend,
    platformHealth: overview?.platform_health || 0,
    complianceScore: overview?.compliance_score || 0,
    activeEnvironments: overview?.active_environments || 0,
    openViolations: overview?.open_violations || 0,
    estimatedSavings: overview?.estimated_savings || 0,
    // Monitoring overview data
    healthyEnvironments: monitoringOverview?.healthy_environments || 0,
    degradedEnvironments: monitoringOverview?.degraded_environments || 0,
    downEnvironments: monitoringOverview?.down_environments || 0,
    avgUptimePercentage: monitoringOverview?.avg_uptime_percentage || 0,
    totalEnvironments: monitoringOverview?.total_environments || 0,
    // FinOps budget metrics
    totalBudget: finopsMetrics?.total_budget || 0,
    totalSpent: finopsMetrics?.total_spent || 0,
    burnRate: finopsMetrics?.burn_rate || 0,
    budgetRemaining: finopsMetrics?.remaining || 0,
    budgetAlerts: finopsMetrics?.alerts || 0,
    // Burn rate trend for charts
    burnRateVsPlanned: burnRateTrend,
  };
};

/**
 * Fetch GenAIOps data source
 */
const fetchGenAIOpsSourceData = async (_user: FetcherUser | undefined, clientId: string | null | undefined, params: DateRangeParams): Promise<Record<string, unknown>> => {
  const [metrics, realtime, modelBreakdown, tracesTimeseries] = await Promise.all([
    getOverviewMetrics({
      client_id: clientId,
      start_date: params.start_date,
      end_date: params.end_date,
    } as any).catch(() => null),
    getRealtimeMetrics({
      client_id: clientId,
      days: params.days,
      start_date: params.start_date,
      end_date: params.end_date,
    } as any).catch(() => null),
    getCostBreakdown({
      days: params.days,
      start_date: params.start_date,
      end_date: params.end_date,
      groupBy: 'model',
      clientId: clientId,
    } as any, undefined, undefined).catch(() => null),
    getTracesTimeseries({
      client_id: clientId,
      start_date: params.start_date,
      end_date: params.end_date,
      hours: params.hours || 17520  // Fallback to 2 years for "All Time"
    }).catch(() => null),
  ]);

  // Extract cost with multiple fallback sources
  // Priority: realtime total_cost (respects date range) -> 24h cost -> metrics total_cost -> model breakdown total
  const totalCost =
    realtime?.total_cost ||              // Respects selected date range
    realtime?.total_cost_last_24h ||
    metrics?.total_cost ||
    modelBreakdown?.total_cost ||
    (modelBreakdown?.breakdown?.reduce((sum: number, m: { total_cost?: number }) => sum + (m.total_cost || 0), 0)) ||
    0;

  // Extract latency metrics with fallbacks
  // Priority: realtime metrics -> metrics aggregates
  const avgLatency =
    realtime?.avg_latency_last_hour_ms ||
    realtime?.avg_latency_ms ||
    metrics?.avg_latency_ms ||
    metrics?.latency?.avg ||
    0;

  const p95Latency =
    realtime?.p95_latency_ms ||
    metrics?.p95_latency_ms ||
    metrics?.latency?.p95 ||
    0;

  const p99Latency =
    realtime?.p99_latency_ms ||
    metrics?.p99_latency_ms ||
    metrics?.latency?.p99 ||
    0;

  // Extract error rate with fallbacks
  const errorRate =
    realtime?.error_rate_last_hour ||
    realtime?.error_rate ||
    metrics?.error_rate ||
    (metrics?.failed_traces && metrics?.total_traces
      ? (metrics.failed_traces / metrics.total_traces) * 100
      : 0);

  // Extract token counts with fallbacks
  const totalTokens =
    realtime?.total_tokens_last_24h ||
    realtime?.total_tokens ||
    metrics?.total_tokens ||
    metrics?.agents?.total_tokens ||
    0;

  return {
    metrics,
    realtime,
    modelBreakdown,
    tracesTimeseries,
    // Pass date range info for widgets to adjust formatting
    // For hourly presets (last_1_hour, last_6_hours), convert hours to fractional days
    dateRangeDays: params.days || (params.hours ? params.hours / 24 : null),
    startDate: params.start_date,
    endDate: params.end_date,
    // Pre-extracted values with comprehensive fallbacks
    totalTraces: metrics?.total_traces || realtime?.total_traces || 0,
    completedTraces: metrics?.completed_traces || realtime?.completed_traces || 0,
    totalCost,
    avgLatency,
    errorRate,
    totalTokens,
    qualityScore: metrics?.quality_score || realtime?.quality_score || 0,
    p95Latency,
    p99Latency,
    uniqueSessions: metrics?.agents?.unique_sessions || metrics?.unique_sessions || 0,
    totalRuns: metrics?.agents?.total_runs || metrics?.total_runs || 0,
  };
};

/**
 * Fetch AgentOps data source
 */
const fetchAgentOpsSourceData = async (_user: FetcherUser | undefined, clientId: string | null | undefined, params: DateRangeParams): Promise<Record<string, unknown>> => {
  const selectedProjectId = (() => {
    try {
      return localStorage.getItem('selectedProjectId');
    } catch {
      return null;
    }
  })();

  const [runsResult, toolsResult, safetyResult, incidentsResult, apmResult] = await Promise.all([
    getAgentRuns({
      client_id: clientId,
      project_id: selectedProjectId,
      limit: 100,
      start_date: params.start_date,
      end_date: params.end_date,
    } as any).catch(() => ({ data: [] })),
    getToolUsage({ client_id: clientId, project_id: selectedProjectId, days: params.days, start_date: params.start_date, end_date: params.end_date } as any).catch(() => ({ tools: [], timeline: [] })),
    getSafetyStats({ client_id: clientId, project_id: selectedProjectId, days: params.days, start_date: params.start_date, end_date: params.end_date } as any).catch(() => ({ total_violations: 0, compliance_rate: 100 })),
    getIncidentStats({ client_id: clientId, project_id: selectedProjectId, days: params.days, start_date: params.start_date, end_date: params.end_date } as any).catch(() => ({ total: 0, incidents: [] })),
    getAPMHealth({
      client_id: clientId,
      project_id: selectedProjectId,
      start_date: params.start_date,
      end_date: params.end_date,
      time_range: params.days ? `${params.days}d` : '30d',
    } as any).catch(() => ({ score: 0, heap_used_percent: 0, cpu_percent: 0 })),
  ]);

  const runs = runsResult?.data || runsResult?.runs || [];
  const tools = toolsResult?.tools || [];
  const incidents = incidentsResult?.incidents || [];

  // Calculate derived metrics
  const activeRuns = runs.filter((r: { status?: string }) => r.status === 'running').length;
  const completedRuns = runs.filter((r: { status?: string }) => r.status === 'completed').length;
  const failedRuns = runs.filter((r: { status?: string }) => r.status === 'failed').length;
  const totalRuns = runs.length;
  const successRate = totalRuns > 0 ? (completedRuns / totalRuns) * 100 : 0;

  // Extract APM metrics with multiple fallback paths
  // The API may return different field names depending on version
  const heapUsage =
    apmResult?.heap_used_percent ||
    apmResult?.heap_usage_percent ||
    apmResult?.memory?.heap_used_percent ||
    apmResult?.metrics?.heap_used_percent ||
    0;

  const cpuUsage =
    apmResult?.cpu_percent ||
    apmResult?.cpu_usage_percent ||
    apmResult?.cpu?.usage_percent ||
    apmResult?.metrics?.cpu_percent ||
    0;

  const healthScore =
    apmResult?.score ||
    apmResult?.health_score ||
    apmResult?.overall_score ||
    apmResult?.metrics?.health_score ||
    0;

  return {
    runs,
    tools,
    toolTimeline: toolsResult?.timeline || [],
    safetyViolations: safetyResult?.total_violations || safetyResult?.violations_count || 0,
    complianceRate: safetyResult?.compliance_rate || 100,
    incidents,
    totalIncidents: incidentsResult?.total || incidentsResult?.total_incidents || 0,
    healthScore,
    heapUsage,
    cpuUsage,
    // Raw APM result for debugging and additional metrics
    apmMetrics: apmResult,
    // Calculated metrics
    activeRuns,
    completedRuns,
    failedRuns,
    totalRuns,
    successRate,
    totalTools: tools.length,
  };
};

/**
 * Determine chart granularity based on date range
 * - ≤24 hours: hourly
 * - ≤7 days: hourly
 * - >90 days: weekly
 * - default: daily
 */
const getChartGranularity = (params: DateRangeParams) => {
  if (params.hours && params.hours <= 24) return 'hour';
  if (params.days && params.days <= 7) return 'hour';
  if (params.days && params.days > 90) return 'week';
  return 'day';
};

/**
 * Fetch DataOps data source
 * Uses lens-dataops microservice APIs (catalogAPI, runsAPI)
 */
const fetchDataOpsSourceData = async (_user: FetcherUser | undefined, _clientId: string | null | undefined, params: DateRangeParams): Promise<Record<string, unknown>> => {
  // DataOps API automatically adds client_id from localStorage via buildQueryString
  // So we don't need to pass it explicitly here
  const apiParams = {
    start_date: params.start_date,
    end_date: params.end_date,
  };

  // Fetch from lens-dataops service
  const [catalogOverview, runsOverview, runsList, eventsOverview] = await Promise.all([
    catalogAPI.getOverview(apiParams).catch(() => ({ total_datasets: 0, total_jobs: 0, total_namespaces: 0 })),
    runsAPI.getOverview(apiParams).catch(() => ({ completed: 0, failed: 0, running: 0, pending: 0, total: 0 })),
    runsAPI.getRuns({ ...apiParams, limit: 1000 }).catch(() => []),
    eventsAPI.getOverview(apiParams).catch(() => ({ total_events: 0, success_rate: 0, failed_jobs: 0, running_jobs: 0 })),
  ]);

  // Determine granularity based on date range
  const granularity = getChartGranularity(params);

  // Transform runs list to timeline chart data with appropriate granularity
  const timelineData = transformRunsToTimeline(runsList, granularity);

  // Calculate run statistics from runsList as fallback (more reliable than overview)
  const runsArray = Array.isArray(runsList) ? runsList : [];
  const calculatedSuccessful = runsArray.filter(r =>
    ['completed', 'success', 'COMPLETED', 'SUCCESS'].includes(r.status)
  ).length;
  const calculatedFailed = runsArray.filter(r =>
    ['failed', 'error', 'FAILED', 'ERROR'].includes(r.status)
  ).length;
  const calculatedRunning = runsArray.filter(r =>
    ['running', 'RUNNING', 'in_progress', 'IN_PROGRESS'].includes(r.status)
  ).length;
  const calculatedPending = runsArray.filter(r =>
    ['pending', 'PENDING', 'queued', 'QUEUED'].includes(r.status)
  ).length;

  // Use overview data if available, otherwise use calculated values from runsList
  const successfulRuns = runsOverview?.completed || calculatedSuccessful;
  const failedRuns = runsOverview?.failed || calculatedFailed;
  const runningRuns = runsOverview?.running || calculatedRunning;
  const pendingRuns = runsOverview?.pending || calculatedPending;
  const totalRuns = runsOverview?.total || runsArray.length;

  // Calculate success rate
  const completedAndFailed = successfulRuns + failedRuns;
  const successRate = completedAndFailed > 0 ? Math.round((successfulRuns / completedAndFailed) * 100) : 0;

  return {
    totalDatasets: catalogOverview?.total_datasets || 0,
    totalJobs: catalogOverview?.total_jobs || 0,
    totalNamespaces: catalogOverview?.total_namespaces || 0,
    // Quality score - calculate from data if available, otherwise use placeholder
    dataQualityScore: catalogOverview?.quality_score || 0,
    // Degraded datasets count
    degradedDatasets: catalogOverview?.degraded_datasets || 0,
    // Run statistics (prefer overview, fallback to calculated from runsList)
    successfulRuns,
    failedRuns,
    runningRuns,
    pendingRuns,
    totalRuns,
    // Aliases for widget compatibility
    runningJobs: runningRuns,
    activePipelines: runningRuns,
    pipelineSuccessRate: successRate,
    // Runs by status for donut chart (ChartWidget expects 'name' key)
    runsByStatus: [
      { name: 'Success', count: successfulRuns },
      { name: 'Failed', count: failedRuns },
      { name: 'Running', count: runningRuns },
    ],
    // Timeline chart data (transformed from runs list)
    pipelineRuns: timelineData,
    pipelineChartConfig: {
      chartType: 'bar',
      xKey: granularity === 'hour' ? 'hour' : 'date',
      yKeys: ['successful', 'failed'],
      colors: [GREEN, RED],
      stacked: true,
    },
    // Events data from eventsAPI
    eventsToday: eventsOverview?.total_events || 0,
    eventsSuccessRate: eventsOverview?.success_rate || 0,
    // Active jobs from catalog (jobs that are currently executing)
    activeJobs: catalogOverview?.active_jobs || 0,
  };
};

/**
 * Transform runs list to timeline chart data
 * Groups runs by date/hour and counts successful/failed
 * @param {Array} runs - List of runs from API
 * @param {string} granularity - 'hour', 'day', or 'week'
 * @returns {Array} Timeline data with format: { date|hour: string, successful: number, failed: number }
 */
interface RunRecord {
  started_at?: string;
  status?: string;
}

interface PeriodBucket {
  date?: string;
  hour?: string;
  successful: number;
  failed: number;
}

const transformRunsToTimeline = (runs: RunRecord[], granularity = 'day'): PeriodBucket[] => {
  if (!Array.isArray(runs) || runs.length === 0) return [];

  const periods: Record<string, PeriodBucket> = {};
  // Use 'hour' key for hourly data, 'date' for daily/weekly
  const timeKey = granularity === 'hour' ? 'hour' : 'date';

  runs.forEach(run => {
    const date = run.started_at ? new Date(run.started_at) : null;
    if (!date || Number.isNaN(date.getTime())) return;

    let periodKey;
    if (granularity === 'hour') {
      // Format: "2024-01-15T14:00" (readable hour format)
      periodKey = date.toISOString().slice(0, 13).replace('T', ' ') + ':00';
    } else if (granularity === 'week') {
      // Get start of week (Sunday)
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      periodKey = weekStart.toISOString().slice(0, 10);
    } else {
      // Default: day (format: "2024-01-15")
      periodKey = date.toISOString().slice(0, 10);
    }

    if (!periods[periodKey]) {
      periods[periodKey] = { [timeKey]: periodKey, successful: 0, failed: 0 };
    }

    if (['completed', 'success', 'COMPLETED', 'SUCCESS'].includes(run.status ?? '')) {
      periods[periodKey].successful++;
    } else if (['failed', 'error', 'FAILED', 'ERROR'].includes(run.status ?? '')) {
      periods[periodKey].failed++;
    }
  });

  // Sort by time period ascending (handles both 'date' and 'hour' keys)
  return Object.values(periods).sort((a, b) => {
    const aTime = a.date || a.hour || '';
    const bTime = b.date || b.hour || '';
    return aTime.localeCompare(bTime);
  });
};

/**
 * Filter items by date range
 */
const filterByDateRange = <T extends Record<string, unknown>>(items: T[], startDate: Date | null, endDate: Date | null, dateField = 'createdAt'): T[] => {
  if (!items || items.length === 0 || !startDate || !endDate) return items;

  return items.filter(item => {
    const itemDate = new Date((item[dateField] || item.updatedAt || item.timestamp) as string | number | Date);
    return itemDate >= startDate && itemDate <= endDate;
  });
};

/**
 * Fetch widget data using its template definition
 * Enhanced with retry logic and validation
 *
 * @param {Object} widget - Widget template with getData function
 * @param {Object} context - Context including dateRange, user, etc.
 * @returns {Object} - Result object with data, success status, and any errors
 */
export const fetchWidgetData = async (widget: Widget, context: FetcherContext): Promise<WidgetFetchResult> => {
  if (!widget) {
    return { data: getEmptyWidgetData(), error: 'No widget provided', success: false };
  }

  if (!widget.id) {
    return { data: getEmptyWidgetData(), error: 'Widget missing id', success: false };
  }

  if (typeof widget.getData !== 'function') {
    return { data: getEmptyWidgetData(widget), error: 'No getData function', success: false };
  }

  try {
    // Fetch with retry logic
    const sourceData = await fetchWithRetry(
      () => fetchDataSourceData(widget.dataSource, context),
      2,
      1000
    );

    if (!sourceData) {
      return {
        data: getEmptyWidgetData(widget),
        error: `Failed to fetch data source: ${widget.dataSource}`,
        success: false,
      };
    }

    // Validate source data
    try {
      validateSourceData(sourceData, widget.dataSource);
    } catch (validationError) {
      // Continue with potentially invalid data - let widget handle it
    }

    // Transform using the widget's getData function
    const rawData = widget.getData(sourceData, context);

    // Validate transformed data shape
    const validation = safeValidateWidgetData(widget.type ?? '', rawData, widget.id);

    if (!validation.valid) {
    }

    return {
      data: rawData as Record<string, unknown>,
      errors: validation.valid ? undefined : validation.errors,
      success: true,
    };
  } catch (error) {
    const errMsg = error instanceof Error ? error.message : String(error);
    console.error(`fetchWidgetData error for widget ${widget.id}:`, {
      error: errMsg,
      dataSource: widget.dataSource,
    });

    return {
      data: getEmptyWidgetData(widget),
      error: errMsg,
      success: false,
    };
  }
};

/**
 * Batch fetch data for multiple widgets
 * Groups widgets by data source to minimize API calls
 * Enhanced with retry logic, validation, and partial success tracking
 *
 * @param {Array} widgets - Array of widget templates
 * @param {Object} context - Context including dateRange, user, etc.
 * @returns {Object} - Object with results map and stats
 */
export const fetchWidgetsData = async (widgets: Widget[], context: FetcherContext): Promise<{ results: Record<string, WidgetFetchResult>; stats: { total: number; success: number; failed: number } }> => {

  if (!widgets || widgets.length === 0) {
    return { results: {}, stats: { total: 0, success: 0, failed: 0 } };
  }

  // Group widgets by data source
  const widgetsBySource: Record<string, Widget[]> = {};
  widgets.forEach((widget) => {
    const source = widget.dataSource || 'unknown';
    if (!widgetsBySource[source]) {
      widgetsBySource[source] = [];
    }
    widgetsBySource[source].push(widget);
  });


  // Fetch all data sources in parallel with retry logic
  const sourceDataPromises = Object.keys(widgetsBySource).map(async source => {
    try {
      const data = await fetchWithRetry(
        () => fetchDataSourceData(source, context),
        2,
        1000
      );
      return { source, data, success: true };
    } catch (error) {
      console.error(`Failed to fetch data source ${source}:`, error);
      return { source, data: null, success: false, error: error instanceof Error ? error.message : String(error) };
    }
  });

  const sourceResults = await Promise.all(sourceDataPromises);

  // Create a map of source -> data
  const sourceDataMap: Record<string, unknown> = {};
  const sourceErrors: Record<string, string> = {};
  sourceResults.forEach(({ source, data, success, error }) => {
    sourceDataMap[source] = data;
    if (!success) {
      sourceErrors[source] = error ?? 'Unknown error';
    }
  });

  // Transform data for each widget
  const results: Record<string, WidgetFetchResult> = {};
  let successCount = 0;
  let failedCount = 0;

  for (const widget of widgets) {
    const sourceData = sourceDataMap[widget.dataSource];

    if (!sourceData) {
      results[widget.id] = {
        data: getEmptyWidgetData(widget),
        success: false,
        error: sourceErrors[widget.dataSource] || 'Data source unavailable',
      };
      failedCount++;
      continue;
    }

    try {
      if (typeof widget.getData === 'function') {
        const rawData = widget.getData(sourceData, context);
        const validation = safeValidateWidgetData(widget.type ?? '', rawData, widget.id);

        results[widget.id] = {
          data: rawData as Record<string, unknown>,
          success: true,
          validationErrors: validation.valid ? undefined : validation.errors,
        };
        successCount++;
      } else {
        results[widget.id] = {
          data: getEmptyWidgetData(widget),
          success: false,
          error: 'No getData function',
        };
        failedCount++;
      }
    } catch (error) {
      console.error(`Error transforming data for widget ${widget.id}:`, error);
      results[widget.id] = {
        data: getEmptyWidgetData(widget),
        success: false,
        error: error instanceof Error ? error.message : String(error),
      };
      failedCount++;
    }
  }

  return {
    results,
    stats: {
      total: widgets.length,
      success: successCount,
      failed: failedCount,
    },
  };
};

/**
 * Get empty/fallback widget data
 */
const getEmptyWidgetData = (widget: Partial<Widget> = {}): Record<string, unknown> => {
  return {
    value: 0,
    label: widget?.name || 'No Data',
    format: 'number',
    color: 'gray',
    error: true,
  };
};

/**
 * Clear all cached data
 */
export const clearCache = () => {
  dataSourceCache = {};
  cacheTimestamps = {};
};

/**
 * Clear cache for a specific data source
 */
export const clearSourceCache = (dataSource: string): void => {
  Object.keys(dataSourceCache).forEach(key => {
    if (key.startsWith(dataSource)) {
      delete dataSourceCache[key];
      delete cacheTimestamps[key];
    }
  });
};

const widgetDataFetcher = {
  fetchWidgetData,
  fetchWidgetsData,
  fetchDataSourceData,
  dateRangeToParams,
  clearCache,
  clearSourceCache,
  setCacheTTL,
  getCacheTTL,
};

export default widgetDataFetcher;
